import React, { useState } from "react";
import { ArrowLeft, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { verifyReportCall } from "../service/ReportService";

const formatDateTime = (ms) => {
    if (!ms) return "—";
    return new Date(ms).toLocaleString(undefined, {
        year: "numeric", month: "long", day: "numeric",
        hour: "numeric", minute: "2-digit",
    });
};

export default function VerifyReport() {
    const navigate = useNavigate();
    const [reportId, setReportId] = useState("");
    const [loading, setLoading] = useState(false);
    const [found, setFound] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [error, setError] = useState(false);
    const [report, setReport] = useState(null);

    const reset = () => { setFound(false); setNotFound(false); setError(false); setReport(null); setReportId(""); };

    const handleVerify = (e) => {
        e?.preventDefault();
        if (!reportId.trim()) return;
        setLoading(true);
        setFound(false); setNotFound(false); setError(false);
        verifyReportCall(reportId.trim(), true)
            .then(res => {
                if (res.success === true) {
                    if (!res.report) { setNotFound(true); }
                    else { setReport(res.report); setFound(true); setReportId(""); }
                } else {
                    setNotFound(true);
                }
            })
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    };

    const overallScore = report
        ? Math.round((report.noveltyScore + report.methodScore + report.impactScore) / 3)
        : null;

    return (
        <div className="home-layout">
            <Sidebar activePage="Settings" />

            <main className="home-main">
                <Header />

                <div className="home-content" style={{ maxWidth: 680 }}>
                    <button className="chpw-back" onClick={() => navigate("/settings")}>
                        <ArrowLeft size={14} />
                        Back to Settings
                    </button>

                    <div className="score-history-card" style={{ marginBottom: 16 }}>
                        <p className="score-history-title">Report Verification</p>
                        <p className="score-history-sub">Enter a report ID to verify its authenticity.</p>
                    </div>

                    {!found ? (
                        <div className="submissions-card" style={{ padding: "24px" }}>
                            <form onSubmit={handleVerify}>
                                <div className="upload-field" style={{ marginBottom: 16 }}>
                                    <label className="upload-label">Report ID</label>
                                    <input
                                        className="upload-input"
                                        type="text"
                                        placeholder="ex: 536bf54d-50ea-4408-9bdc-64a5fb5642bd"
                                        value={reportId}
                                        onChange={e => { setReportId(e.target.value); setNotFound(false); setError(false); }}
                                    />
                                </div>

                                {notFound && (
                                    <div className="sub-status-banner sub-status-error" style={{ marginBottom: 16 }}>
                                        <XCircle size={16} />
                                        <p>No report found for this ID.</p>
                                    </div>
                                )}
                                {error && (
                                    <div className="sub-status-banner sub-status-error" style={{ marginBottom: 16 }}>
                                        <AlertCircle size={16} />
                                        <p>There was an error looking up this report. Please try again.</p>
                                    </div>
                                )}

                                <button className="start-analysis-btn" type="submit" disabled={loading || !reportId.trim()}>
                                    {loading ? "Verifying..." : "Verify Report"}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <div className="submissions-card" style={{ padding: "24px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                                <CheckCircle2 size={18} color="var(--accent-text)" />
                                <p className="similar-heading">Verified Report</p>
                            </div>
                            <p className="score-history-sub" style={{ marginBottom: 20 }}>
                                This report exists in our system and matches the details below.
                            </p>

                            <table className="submissions-table similar-works-table">
                                <tbody>
                                    {[
                                        { label: "Report ID", value: <span style={{ fontFamily: "monospace", fontSize: 12 }}>{report.reportId || "—"}</span> },
                                        { label: "Report Generated", value: formatDateTime(report.generatedDate) },
                                        { label: "Submitted By", value: report.user ? `${report.user.firstName || ""} ${report.user.lastName || ""}`.trim() + (report.user.email ? ` (${report.user.email})` : "") : "—" },
                                        { label: "Manuscript Title", value: report.manuscriptTitle || "—" },
                                        { label: "Manuscript Name", value: report.manuscriptName || "—" },
                                        { label: "Manuscript ID", value: <span style={{ fontFamily: "monospace", fontSize: 12 }}>{report.manuscriptId ?? "—"}</span> },
                                        { label: "Manuscript Uploaded", value: formatDateTime(report.uploadDate) },
                                        { label: "Overall Score", value: overallScore },
                                        { label: "Novelty Score", value: report.noveltyScore },
                                        { label: "Method Score", value: report.methodScore },
                                        { label: "Impact Score", value: report.impactScore },
                                    ].map(({ label, value }) => (
                                        <tr key={label}>
                                            <td style={{ color: "var(--ink-400)", fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", width: 1 }}>{label}</td>
                                            <td>{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <button className="sub-action-btn sub-action-outline" style={{ marginTop: 20, width: "auto" }} onClick={reset}>
                                Verify Another Report
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
